import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import {
  Button,
  Description,
  FieldError,
  Form,
  Heading,
  Input,
  InputGroup,
  Label,
  Link,
  Separator,
  Spinner,
  TextField,
} from '@heroui/react';
import { useAuth } from '../../../hooks/useAuth';
import { authDebug } from '../../../lib/auth-debug';
import { checkBackendHealth } from '../../../utils/backendHealth';
import { signInEmail, signInGoogle, signUpEmail } from '../../../lib/firebase';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { GoogleIcon } from '../../../components/icons/google-icon';
import { AsyncButton } from '../../../components/ui/async-button';
import { StatusCallout } from '../../../components/ui/status-callout';
import { AuthSplitLayout } from '../components/auth-split-layout';
import { InstallAppCta } from '../components/install-app-guide';

import { getPostAuthPath } from '../utils/post-auth-redirect';
import { validateEmail } from '../utils/validate-email';

function validatePassword(value: string, isSignUp: boolean) {
  if (!value) return 'A senha é obrigatória';
  if (value.length < 6) return 'A senha deve ter pelo menos 6 caracteres';
  if (isSignUp && value.length < 8) return 'Use pelo menos 8 caracteres ao criar a conta';
  return null;
}

export default function AuthLoginPage() {
  const { authUser, userDoc, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  useEffect(() => {
    checkBackendHealth().then(setBackendAvailable);
  }, []);

  useEffect(() => {
    setAttemptedSubmit(false);
    setFieldErrors({});
    setEmail('');
    setPassword('');
  }, [isSignUp]);

  useEffect(() => {
    if (!attemptedSubmit) return;
    setFieldErrors({
      email: validateEmail(email) ?? undefined,
      password: validatePassword(password, isSignUp) ?? undefined,
    });
  }, [email, password, isSignUp, attemptedSubmit]);

  useEffect(() => {
    if (authLoading || !authUser || !userDoc) return;
    const isEmailProvider = authUser.providerData.some((p) => p.providerId === 'password');
    if (isEmailProvider && !authUser.emailVerified) {
      navigate('/verify-email');
      return;
    }
    if (!userDoc.customerCompleted && !userDoc.isGlobalAdmin) {
      authDebug('Login redirect → /complete-user', {
        email: userDoc.email,
        isGlobalAdmin: userDoc.isGlobalAdmin,
        customerCompleted: userDoc.customerCompleted,
      });
      navigate('/complete-user');
      return;
    }

    const destination = getPostAuthPath(userDoc);
    authDebug(`Login redirect → ${destination}`, {
      email: userDoc.email,
      isGlobalAdmin: userDoc.isGlobalAdmin,
    });
    navigate(destination);
  }, [authLoading, authUser, userDoc, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!backendAvailable || submitting) return;

    setAttemptedSubmit(true);

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password, isSignUp);
    setFieldErrors({
      email: emailError ?? undefined,
      password: passwordError ?? undefined,
    });
    if (emailError || passwordError) return;

    setSubmitting(true);
    try {
      if (isSignUp) {
        await signUpEmail(email, password);
        showSuccess('Conta criada! Verifique seu email.');
        navigate('/verify-email');
      } else {
        await signInEmail(email, password);
        showSuccess('Login realizado com sucesso!');
      }
    } catch (err) {
      showErrorFromUnknown(err, 'Erro de autenticação');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    if (!backendAvailable || submitting) return;
    setSubmitting(true);
    try {
      await signInGoogle();
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao entrar com Google');
    } finally {
      setSubmitting(false);
    }
  };

  // Evita flash do formulário enquanto Firebase restaura a sessão (PWA/mobile).
  if (authLoading || (authUser && !userDoc)) {
    return (
      <AuthSplitLayout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout>
        <div className="w-full max-w-sm space-y-6">
          <div className="flex w-full flex-col items-center space-y-1 text-center lg:items-start lg:text-left">
            <Heading level={2} className="w-full text-center text-2xl font-semibold lg:text-left">
              {isSignUp ? 'Criar conta' : 'Bem-vindo de volta'}
            </Heading>
            <Description className="text-center lg:text-left">
              {isSignUp
                ? 'Preencha os dados para começar'
                : 'Entre com email ou Google para continuar'}
            </Description>
          </div>

          <InstallAppCta />

          {!backendAvailable ? (
            <StatusCallout status="warning" title="Servidor indisponível">
              O backend não respondeu. Tente novamente em instantes.
            </StatusCallout>
          ) : null}

          <Form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <TextField
              name="email"
              type="email"
              fullWidth
              isInvalid={attemptedSubmit && !!fieldErrors.email}
            >
              <Label>Email</Label>
              <Input
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                autoComplete="email"
                className="h-11 text-base"
              />
              {attemptedSubmit && fieldErrors.email ? (
                <FieldError>{fieldErrors.email}</FieldError>
              ) : null}
            </TextField>

            <TextField
              name="password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              isInvalid={attemptedSubmit && !!fieldErrors.password}
            >
              <Label>Senha</Label>
              <InputGroup fullWidth>
                <InputGroup.Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  className="h-11 text-base"
                />
                <InputGroup.Suffix>
                  <Button
                    isIconOnly
                    size="lg"
                    variant="ghost"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    onPress={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </Button>
                </InputGroup.Suffix>
              </InputGroup>
              {attemptedSubmit && fieldErrors.password ? (
                <FieldError>{fieldErrors.password}</FieldError>
              ) : null}
            </TextField>

            {!isSignUp ? (
              <div className="flex justify-end">
                <Link className="text-sm" onPress={() => navigate('/forgot-password')}>
                  Esqueci a senha
                </Link>
              </div>
            ) : null}

            <AsyncButton
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isPending={submitting}
              isDisabled={!backendAvailable}
              pendingLabel={isSignUp ? 'Criar conta' : 'Entrar'}
            >
              {isSignUp ? 'Criar conta' : 'Entrar'}
            </AsyncButton>
          </Form>

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-muted text-xs uppercase tracking-wide">ou</span>
            <Separator className="flex-1" />
          </div>

          <AsyncButton
            variant="secondary"
            size="lg"
            fullWidth
            onPress={handleGoogle}
            isPending={submitting}
            isDisabled={!backendAvailable}
            pendingLabel="Continuar com Google"
          >
            <GoogleIcon size={20} />
            Continuar com Google
          </AsyncButton>

          <p className="text-muted text-center text-sm">
            {isSignUp ? 'Já tem conta?' : 'Ainda não tem conta?'}{' '}
            <Link
              className="text-sm font-medium text-accent"
              onPress={() => setIsSignUp((v) => !v)}
            >
              {isSignUp ? 'Entrar' : 'Criar conta'}
            </Link>
          </p>

          {isSignUp ? (
            <p className="text-muted text-center text-xs leading-relaxed">
              Ao criar uma conta, você concorda com nossa{' '}
              <a
                href="/privacidade"
                className="text-accent font-medium underline-offset-2 hover:underline"
                onClick={(event) => {
                  event.preventDefault();
                  navigate('/privacidade');
                }}
              >
                Política de Privacidade
              </a>
              .
            </p>
          ) : null}
        </div>
    </AuthSplitLayout>
  );
}
